import { useEffect, useRef } from "react";
import { ArrowUpRight, MessageSquare, Monitor, Workflow, Terminal } from "lucide-react";
import { creditUnit } from "@/components/alloy/alloyView";
import { number, dateLabel } from "./sessionData";

export const COMPARISON_MODELS = [
  { id: "openai/gpt-6-astra", label: "GPT 6 Astra Cost" },
  { id: "anthropic/claude-fable-5.1", label: "Claude Fable 5.1 Cost" },
];
const icons = { Dashboard: Monitor, Slack: MessageSquare, Automations: Workflow };
const DOLLARS = creditUnit();

// The list shows full estimates only: every request in the session priced. A
// partial sum next to the full Alloy charge would read as "cheaper than Alloy".
export function fullComparison(estimate, modelId) {
  if (estimate?.status !== "ready") return null;
  const comparison = estimate.summary.comparisons.find((item) => item.id === modelId);
  return comparison && comparison.estimatedCostUsd != null && comparison.excludedRecords === 0 && comparison.coveredRequests === estimate.summary.totals.requestCount ? comparison : null;
}

function ComparisonCell({ model, estimate, unit }) {
  const comparison = fullComparison(estimate, model.id);
  const value = comparison?.estimatedCostUsd;
  const notFull = estimate?.status === "partial" || (estimate?.status === "ready" && estimate.summary.comparisons.some((item) => item.id === model.id && item.estimatedCostUsd != null));
  const title = comparison ? `All ${number(comparison.coveredRequests)} requests priced. Open the session for assumptions.` : "Full estimate unavailable. Open the session for cost details.";
  return (
    <div role="cell" className="session-numeric session-comparison-cell" title={title} data-testid={`comparison-${model.id}`}>
      <span className="session-mobile-label">{model.label}</span>
      <strong>{unit.cost(value)}</strong>
      <small>{!estimate ? "Pending" : estimate.status === "loading" ? "Loading…" : value != null ? "Estimated" : notFull ? "Open for details" : "Unavailable"}</small>
    </div>
  );
}

export default function SessionRow({ item, estimate, requestEstimate, onSelect, unit = DOLLARS }) {
  const row = useRef(null);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((changes) => {
      if (changes.some((entry) => entry.isIntersecting)) {
        requestEstimate(item.id);
        observer.disconnect();
      }
    }, { rootMargin: "120px" });
    observer.observe(row.current);
    return () => observer.disconnect();
  }, [item.id, requestEstimate]);
  const Icon = icons[item.source] || Terminal;
  return (
    <div role="row" className="session-table-row" ref={row}>
      <div role="cell" className="session-title-cell">
        <span className={`session-source-icon session-source-icon-${(item.source || "Agent").toLowerCase()}`} aria-hidden="true"><Icon size={16} /></span>
        <div>
          <button type="button" data-testid={`session-${item.id}`} onClick={() => onSelect(item)}><span>{item.title || `Session ${item.id}`}</span></button>
          <small>{dateLabel(item.updatedAt || item.startedAt)}{item.archived ? " · Archived" : ""}</small>
        </div>
      </div>
      <div role="cell" className="session-source-cell"><span className="session-source-badge">{item.source || "Agent/API"}</span></div>
      <div role="cell" className="session-numeric session-usage-cell"><span>{number(item.totalTokens)} <span className="session-inline-muted">{unit.tokens ? "model\u00a0tokens" : "tokens"}</span></span><small>{number(item.requestCount)} requests</small></div>
      <div role="cell" className="session-numeric session-row-cost"><span className="session-mobile-label">Your Cost With Alloy</span><strong>{unit.cost(item.actualCostUsd)}</strong><small>{item.actualCostUsd == null ? "Not recorded" : "Recorded"}</small></div>
      {COMPARISON_MODELS.map((model) => <ComparisonCell key={model.id} model={model} estimate={estimate} unit={unit} />)}
      <span aria-hidden="true" className="session-row-arrow"><ArrowUpRight size={15} /></span>
    </div>
  );
}
